import axios from 'axios';
import { getAuthToken, clearAuth, isTokenExpired } from '../utils/auth';

// Configuration de base d'axios
// Détermine dynamiquement l'URL de base de l'API selon l'environnement
const resolveApiBaseUrl = () => {
    const envUrl = process.env.REACT_APP_API_BASE_URL;
    if (envUrl && typeof envUrl === 'string' && envUrl.trim()) {
        return envUrl.trim();
    }

    // Heuristique pratique pour le développement local sans proxy
    if (typeof window !== 'undefined') {
        const { hostname, port } = window.location;
        const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
        const isCommonDevPort = port === '3000' || port === '5173'; // CRA/Vite
        if (isLocalhost && isCommonDevPort) {
            return 'http://localhost:5000/api';
        }
    }

    // Par défaut, utiliser le chemin /api pour laisser Nginx/CRA proxy router vers le backend
    return '/api';
};

const API = axios.create({
    baseURL: resolveApiBaseUrl(),
    headers: {
        'Content-Type': 'application/json'
    }
});

// Intercepteur pour ajouter le token à chaque requête
API.interceptors.request.use(
    (config) => {
        const token = getAuthToken();

        if (token) {
            // S'assurer que l'on travaille avec un JWT nu (sans "Bearer ")
            const jwt = String(token).replace(/^Bearer\s+/i, '').trim();

            // Vérifier si le token est expiré avant de l'envoyer
            if (isTokenExpired(jwt)) {
                // Token expiré: nettoyer et laisser l'application gérer la redirection
                clearAuth();
                return Promise.reject(new Error('Token expiré'));
            }

            // Construire l'en-tête Authorization avec un unique préfixe Bearer
            config.headers.Authorization = `Bearer ${jwt}`;
            // Compatibilité avec certaines APIs qui attendent un header alternatif
            config.headers['x-auth-token'] = jwt;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Intercepteur pour gérer les erreurs de réponse
API.interceptors.response.use(
    (response) => response,
    (error) => {
        return Promise.reject(error);
    }
);

// Services d'authentification
export const authAPI = {
    register: (userData) => API.post('/auth/register', userData),
    login: (credentials) => API.post('/auth/login', credentials),
    getMe: () => API.get('/auth/me'),
    logout: () => API.post('/auth/logout'),
    changePassword: (payload) => API.post('/auth/change-password', payload),
    requestPasswordReset: (email) => API.post('/auth/forgot-password', { email }),
    resetPassword: ({ token, password }) => API.post('/auth/reset-password', { token, password })
};

// Services utilisateurs
export const userAPI = {
    getUsers: () => API.get('/users'),
    getUser: (id) => API.get(`/users/${id}`),
    updatePreferences: (prefs) => API.post('/users/preferences', prefs),
    updateProfile: (data) => API.put('/users/me', data)
};

// Services transactions
export const transactionAPI = {
    // Récupérer toutes les transactions
    getTransactions: (params = {}) => {
        const queryParams = new URLSearchParams();

        if (params.type) queryParams.append('type', params.type);
        if (params.categoryId) queryParams.append('categoryId', params.categoryId);
        if (params.startDate) queryParams.append('startDate', params.startDate);
        if (params.endDate) queryParams.append('endDate', params.endDate);
        if (params.page) queryParams.append('page', params.page);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.accountId) queryParams.append('accountId', params.accountId);

        const qs = queryParams.toString();
        return API.get(qs ? `/transactions?${qs}` : '/transactions');
    },

    // Créer une transaction
    createTransaction: (transactionData) => API.post('/transactions', transactionData),

    // Récupérer une transaction par ID
    getTransaction: (id) => API.get(`/transactions/${id}`),

    // Mettre à jour une transaction
    updateTransaction: (id, transactionData) => API.put(`/transactions/${id}`, transactionData),

    // Supprimer une transaction
    deleteTransaction: (id) => API.delete(`/transactions/${id}`),

    // Récupérer les statistiques
    getStats: (params = {}) => {
        const queryParams = new URLSearchParams();

        if (params.startDate) queryParams.append('startDate', params.startDate);
        if (params.endDate) queryParams.append('endDate', params.endDate);

        const qs = queryParams.toString();
        return API.get(qs ? `/transactions/stats?${qs}` : '/transactions/stats');
    }
};

// Services abonnements
export const subscriptionsAPI = {
    list: (accountId) => {
        const qs = accountId ? `?accountId=${accountId}` : '';
        return API.get(`/subscriptions${qs}`);
    },
    create: (data) => API.post('/subscriptions', data),
    update: (id, data) => API.put(`/subscriptions/${id}`, data),
    toggle: (id) => API.patch(`/subscriptions/${id}/toggle`),
    remove: (id) => API.delete(`/subscriptions/${id}`),
    process: () => API.post('/subscriptions/process'),
};

// Services comptes
export const accountsAPI = {
    list: () => API.get('/accounts'),
    create: (payload) => API.post('/accounts', payload),
    update: (id, payload) => API.put(`/accounts/${id}`, payload),
    remove: (id) => API.delete(`/accounts/${id}`),
    balances: (year) => API.get(`/accounts/balances?year=${year || new Date().getFullYear()}`)
};

// Services Winamax
export const winamaxAPI = {
    list:   ()         => API.get('/winamax'),
    create: (data)     => API.post('/winamax', data),
    update: (id, data) => API.put(`/winamax/${id}`, data),
    remove: (id)       => API.delete(`/winamax/${id}`),
};

export default API;
